import type React from "react";
import { useId, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AlertOctagon, MailX } from "lucide-react";
import type { reportingPeriod } from "@duress/shared";
import { fmtInt } from "@/lib/format";
import { BRAND } from "@/lib/colors";
import { ChartTooltip, useChartTheme } from "@/lib/chart";
import { Badge } from "@/components/ui/badge";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { EmptyState } from "@/components/EmptyState";
import { ErrorCard } from "@/components/ErrorCard";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { BreakdownBars, type BreakdownBar } from "../dashboard/BreakdownBars";
import { KPIGrid, type KPITile } from "../dashboard/KPIGrid";
import { useDashboardView, type SelectedView } from "@/features/periods/usePeriods";
import { EmergencyMapTab } from "./EmergencyMap";

type EmergencyData = reportingPeriod.EmergencyData;
type EmergencyStoreEntry = reportingPeriod.EmergencyStoreEntry;
type EmergencyStatusEntry = reportingPeriod.EmergencyStatusEntry;
type EmergencyDeviceTypeEntry = reportingPeriod.EmergencyDeviceTypeEntry;

const NOTABLE_VISIBLE_CAP = 5;
const TRIGGER_VISIBLE_CAP = 8;

// ── Cell renderers ────────────────────────────────────────────────────────────

const numCell = (v: unknown): React.ReactNode => (
  <span className="num">{fmtInt(typeof v === "number" ? v : Number(v))}</span>
);

const teamCell = (v: unknown): React.ReactNode => (
  <span className="font-head font-medium">{typeof v === "string" ? v : ""}</span>
);

const labelCell = (v: unknown): React.ReactNode => (
  <span className="font-head">{typeof v === "string" ? v : ""}</span>
);

// ── Columns ──────────────────────────────────────────────────────────────────

const TEAM_COLUMNS: DataTableColumn<EmergencyStoreEntry>[] = [
  { key: "store", label: "Team", render: teamCell },
  { key: "events", label: "Events", render: numCell },
];

const DEVICE_COLUMNS: DataTableColumn<EmergencyDeviceTypeEntry>[] = [
  { key: "device_type", label: "Device Type", render: labelCell },
  { key: "events", label: "Events", render: numCell },
];

// ── Tile helpers ──────────────────────────────────────────────────────────────

function buildTiles(em: EmergencyData): KPITile[] {
  // genuine_events/deescalated_events come pre-classified from the backend
  // (methodology.ts's isGenuineEmergencyStatus/isDeescalatedStatus) — falls
  // back to a dash rather than a misleading 0 when the CSV had no status
  // column to classify at all, same as before.
  const genuineEvents = em.by_status.length > 0 ? em.genuine_events + em.deescalated_events : null;

  const topTeam = em.by_store[0] ?? null;

  return [
    {
      label: "Total Events",
      value: fmtInt(em.total_events + em.testing_events),
      sub: "includes testing, false alarm, etc.",
    },
    {
      label: "Genuine Events",
      value: genuineEvents === null ? "—" : fmtInt(genuineEvents),
      sub: "emergency + de-escalated",
    },
    {
      label: "Active Teams",
      value: fmtInt(em.by_store.length),
    },
    {
      label: "Top Team",
      value: topTeam ? topTeam.store : "—",
      ...(topTeam ? { sub: `${fmtInt(topTeam.events)} events` } : {}),
    },
  ];
}

// ── Status breakdown ────────────────────────────────────────────────────────

// Maps a free-text CSV status to a semantic brand colour. Keyword-matched so
// label variations ("De-escalated", "Deescalated") still resolve correctly.
function statusColor(status: string): string {
  const s = status.toLowerCase();
  if (s.includes("genuine") || s.includes("emergency")) return BRAND.red;
  if (s.includes("deescalat") || s.includes("de-escalat") || s.includes("de escalat")) {
    return BRAND.orange;
  }
  if (s.includes("not verified") || s.includes("unverified") || s.includes("no answer")) {
    return BRAND.blue;
  }
  if (s.includes("false")) return BRAND.green;
  if (s.includes("test")) return "hsl(0 0% 55%)";
  return BRAND.blue;
}

function StatusBreakdown({ entries }: { entries: EmergencyStatusEntry[] }): React.JSX.Element {
  const max = Math.max(...entries.map((e) => e.events), 1);
  const bars: BreakdownBar[] = [...entries]
    .sort((a, b) => b.events - a.events)
    .map((entry) => ({
      label: entry.status,
      display: fmtInt(entry.events),
      color: statusColor(entry.status),
      pct: (entry.events / max) * 100,
    }));

  return <BreakdownBars bars={bars} />;
}

// ── Notable card ──────────────────────────────────────────────────────────────

const NOTABLE_KEYS = ["store", "user", "device", "time"] as const;

function NotableCard({ entry }: { entry: Record<string, string> }): React.JSX.Element {
  const note = entry["note"] ?? "";
  const status = entry["status"] ?? "";
  const footerParts = NOTABLE_KEYS.map((k) => entry[k]).filter(
    (v): v is string => typeof v === "string" && v.length > 0,
  );

  return (
    <div className="card-duress p-4" data-notable-card>
      <p className="text-sm text-foreground whitespace-pre-wrap">{note}</p>
      {(status || footerParts.length > 0) && (
        <div className="flex flex-wrap items-center gap-2 mt-2">
          {status && (
            <Badge
              className="border-transparent text-white font-semibold"
              style={{ backgroundColor: statusColor(status) }}
            >
              {status}
            </Badge>
          )}
          {footerParts.length > 0 && (
            <p className="text-xs text-muted-foreground font-body">{footerParts.join(" · ")}</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Pure presentational component ────────────────────────────────────────────

export interface EmergencyActivityProps {
  data: reportingPeriod.DashboardData;
}

export function EmergencyActivity({ data }: EmergencyActivityProps): React.JSX.Element {
  const emergency = data.emergency;
  const chart = useChartTheme();
  const [notableExpanded, setNotableExpanded] = useState(false);
  const timelineTitleId = useId();
  const triggerTitleId = useId();

  if (emergency === null) {
    return (
      <div data-emergency-state="not-uploaded">
        <EmptyState
          icon={AlertOctagon}
          body="This period has no emergency data uploaded. Re-upload the period with an emergency CSV to see this tab."
        />
      </div>
    );
  }

  if (emergency.total_events === 0 && emergency.testing_events === 0) {
    return (
      <div data-emergency-state="empty-csv">
        <EmptyState
          icon={MailX}
          body="An emergency CSV was uploaded but contained 0 events for this period."
        />
      </div>
    );
  }

  const tiles = buildTiles(emergency);
  const triggerData = emergency.by_trigger.slice(0, TRIGGER_VISIBLE_CAP);
  const triggerOverflow = emergency.by_trigger.length - triggerData.length;
  const visibleNotable = notableExpanded
    ? emergency.notable
    : emergency.notable.slice(0, NOTABLE_VISIBLE_CAP);
  const notableOverflow = emergency.notable.length - visibleNotable.length;

  return (
    <div className="space-y-6">
      <KPIGrid tiles={tiles} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-[10px]">
        {/* Timeline chart */}
        <div className="card-duress p-5">
          <p id={timelineTitleId} className="font-head font-semibold text-sm text-foreground mb-4">
            Events Over Time
          </p>
          {emergency.timeline.length === 0 ? (
            <div className="py-12 text-center" data-emergency-timeline="empty">
              <p className="text-sm text-muted-foreground">
                No timeline data — emergency CSV is missing a time column.
              </p>
            </div>
          ) : (
            <div role="img" aria-labelledby={timelineTitleId}>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart
                data={emergency.timeline}
                margin={{ left: 10, right: 20, top: 4, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="emergencyArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={BRAND.red} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={BRAND.red} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fontFamily: "Inter", fill: chart.axis }}
                  stroke={chart.grid}
                />
                <YAxis
                  tick={{ fontSize: 11, fontFamily: "Inter", fill: chart.axis }}
                  stroke={chart.grid}
                  allowDecimals={false}
                />
                <Tooltip content={<ChartTooltip />} cursor={{ stroke: chart.grid }} />
                <Area
                  type="monotone"
                  dataKey="events"
                  name="Events"
                  stroke={BRAND.red}
                  strokeWidth={2}
                  fill="url(#emergencyArea)"
                  dot={false}
                  activeDot={{ r: 4, strokeWidth: 0 }}
                />
              </AreaChart>
            </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Status breakdown — the headline figures highlighted to clients */}
        <div className="card-duress p-5" data-emergency-panel="status">
          <p className="font-head font-semibold text-sm text-foreground mb-4">
            Events by Status
          </p>
          {emergency.by_status.length === 0 ? (
            <div className="py-12 text-center" data-emergency-status="empty">
              <p className="text-sm text-muted-foreground">
                No status data — emergency CSV is missing a Status column.
              </p>
            </div>
          ) : (
            <StatusBreakdown entries={emergency.by_status} />
          )}
        </div>
      </div>

      {/* Team breakdown */}
      <div className="card-duress p-5">
        <p className="font-head font-semibold text-sm text-foreground mb-4">
          Events by Team
        </p>
        {emergency.by_store.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-sm text-muted-foreground">
              No team data in this period's emergency CSV.
            </p>
          </div>
        ) : (
          <DataTable<EmergencyStoreEntry>
            columns={TEAM_COLUMNS}
            data={emergency.by_store}
            defaultSort={{ key: "events", dir: "desc" }}
            searchKeys={["store"]}
          />
        )}
      </div>

      {/* Trigger + Device Type panels (secondary detail) */}
      {(triggerData.length > 0 || emergency.by_device_type.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-[10px]">
          {triggerData.length > 0 && (
            <div className="card-duress p-5" data-emergency-panel="trigger">
              <p id={triggerTitleId} className="font-head font-semibold text-sm text-foreground mb-4">
                Events by Trigger
                {triggerOverflow > 0 && (
                  <span className="ml-1.5 font-normal text-xs text-muted-foreground">
                    (top {TRIGGER_VISIBLE_CAP} of {emergency.by_trigger.length})
                  </span>
                )}
              </p>
              <div role="img" aria-labelledby={triggerTitleId}>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart
                  data={triggerData}
                  layout="vertical"
                  margin={{ left: 10, right: 20, top: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={chart.grid} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11, fontFamily: "Inter", fill: chart.axis }}
                    stroke={chart.grid}
                    allowDecimals={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="trigger"
                    width={120}
                    tick={{ fontSize: 11, fontFamily: "Inter", fill: chart.axis }}
                    stroke={chart.grid}
                  />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: chart.grid, fillOpacity: 0.3 }} />
                  <Bar dataKey="events" name="Events" radius={[0, 4, 4, 0]}>
                    {triggerData.map((_, i) => (
                      <Cell key={i} fill={i === 0 ? BRAND.red : BRAND.blue} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              </div>
            </div>
          )}
          {emergency.by_device_type.length > 0 && (
            <div className="card-duress p-5" data-emergency-panel="device-type">
              <p className="font-head font-semibold text-sm text-foreground mb-4">
                Events by Device Type
              </p>
              <DataTable<EmergencyDeviceTypeEntry>
                columns={DEVICE_COLUMNS}
                data={emergency.by_device_type}
                defaultSort={{ key: "events", dir: "desc" }}
              />
            </div>
          )}
        </div>
      )}

      {/* Notable events */}
      {visibleNotable.length > 0 && (
        <div className="space-y-3" data-emergency-section="notable">
          <p className="font-head font-semibold text-sm text-foreground">
            Notable Events
          </p>
          <div className="space-y-2">
            {visibleNotable.map((entry, i) => (
              <NotableCard key={i} entry={entry} />
            ))}
          </div>
          {notableOverflow > 0 && (
            <button
              type="button"
              onClick={() => {
                setNotableExpanded(true);
              }}
              className="text-xs font-medium text-phoenix-orange hover:underline"
            >
              Show all {fmtInt(emergency.notable.length)}
            </button>
          )}
          {notableExpanded && emergency.notable.length > NOTABLE_VISIBLE_CAP && (
            <button
              type="button"
              onClick={() => {
                setNotableExpanded(false);
              }}
              className="text-xs font-medium text-phoenix-orange hover:underline"
            >
              Show first {NOTABLE_VISIBLE_CAP}
            </button>
          )}
        </div>
      )}

      {/* Methodology note */}
      <div className="finding-blue">
        <p className="font-head font-semibold text-sm mb-1 text-pill-blue">Methodology Note</p>
        <p className="text-sm text-foreground">
          <strong>Total Events</strong> includes every activation, including{" "}
          <strong>testing events</strong> — rows whose status contains the word “test”.{" "}
          <strong>Genuine Events</strong> counts only rows classified as Emergency or
          De-escalated. <strong>Notable events</strong> are Genuine or De-escalated rows whose
          operator note exceeded the minimum word count threshold — a testing or false-alarm row
          doesn't qualify no matter how long its note is. Device type labels like <em>Falcon</em>{" "}
          and <em>Eagle</em> come from the processor’s internal device-type mapping. Team
          grouping uses the <em>Team Name</em> column from the source CSV.
        </p>
      </div>
    </div>
  );
}

// ── Smart wrapper that fetches data ───────────────────────────────────────────

export function EmergencyActivityTab({
  clientId,
  view,
}: {
  clientId: string;
  view: SelectedView | null;
}): React.JSX.Element {
  const { data, isLoading, error, refetch } = useDashboardView(clientId, view);

  if (!view) {
    return <EmptyState body="Select a reporting period above." />;
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <LoadingSkeleton variant="kpis" />
        <LoadingSkeleton variant="chart" />
      </div>
    );
  }

  if (error || !data) {
    return <ErrorCard message="Failed to load period data." onRetry={refetch} />;
  }

  return (
    <div className="space-y-6">
      <EmergencyActivity data={data} />
      {/* Suppressed alongside EmergencyActivity's own "not uploaded" empty
          state above, rather than showing two stacked empty states when this
          period has no emergency CSV at all. */}
      {data.emergency !== null && <EmergencyMapTab clientId={clientId} view={view} />}
    </div>
  );
}
